import { Button, Card, CardActions, CardContent, colors, Grid, Modal, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import useErrorCatcher from "utils/useErrorCatcher";
import validate from "validate.js";
import { Wallet } from 'services';
import Currency from "utils/currency";

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 450,
    maxHeight: '100%',
    overflowY: 'auto',
    maxWidth: '100%'
  },
  container: {
    marginTop: theme.spacing(3)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  saveButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  }
}));

const schema = {
  remarks: {
    presence: { allowEmpty: false, message: "is required" },
  },
  amount: {
    presence: { allowEmpty: false, message: "is required" }
  },
};

let mounted = false;

const CreateTransactionModal = props => {
  let { onCreate, reload, open, onClose, className, wallet_id, ...rest } = props;

  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const runAsyncBlock = useErrorCatcher();

  const [formState, setFormState] = useState({
    isValid: false,
    values: {
      remarks: "",
      amount: ""
    },
    touched: {},
    errors: {}
  });

  useEffect(() => {
    const errors = validate(formState.values, schema);

    mounted && setFormState(formState => ({
      ...formState,
      errors: errors || {}
    }));
  }, [formState.values]);

  useEffect(() => {
    mounted = true;
    return () => { mounted = false };
  }, []);

  useEffect(() => {
    setFormState({
      isValid: false,
      values: {
        remarks: "",
        amount: ""
      },
      touched: {},
      errors: {}
    });
  }, [open]);

  if (!open) {
    return null;
  }


  const handleSubmit = event => {
    event.preventDefault();

    setFormState({
      ...formState,
      touched: {
        amount: true,
        remarks: true
      }
    });

    if (Object.keys(formState.errors).length) return;

    mounted && setLoading(true);
    runAsyncBlock(async () => {
      const { amount, remarks } = formState.values;
      await Wallet.transact(wallet_id, { amount: Currency.uncompress(amount), remarks });
      onCreate();
      reload();
    }).finally(() => {
      mounted && setLoading(false);
      onClose();
    });

  }

  const handleFieldChange = event => {
    event.persist();
    mounted && setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true
      }
    }));
  };

  return (
    <Modal open={open}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <form>
          <CardContent>
            <Typography align="center" gutterBottom variant="h3">
              Create Transaction
            </Typography>
            <Grid className={classes.container} container spacing={3}>
              <Grid item md={12} xs={12}>
                <CustTextField type="number" name="amount" label="Amount" formState={formState} handleFieldChange={handleFieldChange} />
              </Grid>
              <Grid item md={12} xs={12}>
                <CustTextField name="remarks" label="Remarks" formState={formState} handleFieldChange={handleFieldChange} />
              </Grid>
            </Grid>
          </CardContent>
          <CardActions className={classes.actions}>
            <Button onClick={onClose} variant="contained">Close</Button>
            <Button className={classes.saveButton} onClick={handleSubmit} variant="contained" disabled={loading} >Create</Button>
          </CardActions>
        </form>
      </Card>
    </Modal>
  );
}



const CustTextField = props => {
  let { label, name, formState, handleFieldChange, type, dropdown, options, required } = props;
  const hasError = field => formState.touched[field] && formState.errors[field] ? true : false;

  return (
    <TextField
      fullWidth
      label={label}
      helperText={hasError(name) ? formState.errors[name][0] : null}
      name={name}
      onChange={handleFieldChange}
      value={formState.values[name]}
      error={hasError(name)}
      variant="outlined"
      type={type}
      select={dropdown}
      SelectProps={{ native: true }}
      InputLabelProps={{ shrink: dropdown }}
      required={required}
    >
      {options && options.length > 0 && options.map((opt, index) => (
        <option key={index} value={opt.value}>{opt.label}</option>
      ))
      }
    </TextField>
  );
}


export default CreateTransactionModal;